/* NPM */
import { useEffect, useState, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import cx from "classnames";
/* UTILS */
import t, { AsHtml } from "../../../utils/translate";
import { useBundle } from "../../../utils/useBundle";
import { setPageData, signupPageView } from "../../../utils/analytics";
import { useCompoundEligibility } from "../../../utils/useCompoundEligibility";
import routes from "../../../routes";
import {
  MODAL_DESCRIPTION,
  OFFER_PLAN_NAME,
  BUNDLE_ID,
  TREATMENT,
  COMPOUND
} from "../constants";
import useAppContext from "../../AppContext";
import applangaKeyMap from "./components/MedsCard/applangaKeyMap";
import getAidFromCookie from "utils/getAidFromCookie";
/* COMPONENTS */
import BulletItem from "../../BulletItem";
import Column from "../../Column";
import SequenceFooter from "../../SequenceFooter";
import RoundButton from "../../RoundButton";
import SiteWrapper from "../../SiteWrapper";
import SimpleOfferCard from "./components/OfferCards/Simple";
import Loading from "../../Loading";
import Error from "../Error";
import Message from "./components/Message";
import MedsCard from "./components/MedsCard";
import StickyFooter from "components/StickyFooter";
import useUrlSlugContext from "components/UrlSlugContext";
/* STYLES */
import styles from "./styles.module.scss";

const ClinicCheckout = () => {
  const history = useHistory();
  const { search } = useLocation();
  const { profile, newQuiz } = useAppContext();
  const { baseUrl } = useUrlSlugContext();
  const { isIAP, isRegistered } = profile;
  const isActiveUser = isIAP === false && isRegistered === false;
  const { compoundEligible } = useCompoundEligibility();

  const isUni3or4Quiz = newQuiz.isUni3 || newQuiz.isUni4;
  const urlParams = new URLSearchParams(search || "");
  const treatment = urlParams.get(TREATMENT);
  const paramBundleId = urlParams.get(BUNDLE_ID);
  const backupBundleId = t("NEW_JS_CLINIC_BUNDLE_ID");
  const showCompoundUI = compoundEligible && treatment === COMPOUND;

  const bundleId = paramBundleId || backupBundleId;

  const { bundle, plans, isLoading, isError, error, retry } = useBundle({
    bundleId,
    isCompound: showCompoundUI
  });

  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    if (bundle && plans.length) {
      let plan = plans.find(p => p.id === bundle.preSelectedOfferPlanId);

      if (!plan) {
        plan = plans[0];
      }

      setSelectedPlan(plan);
    }
  }, [bundle, plans]);

  useEffect(() => {
    setPageData({
      page_category: "clinic",
      site_section: "clinical",
      signup_program: "clinical"
    });
    signupPageView("plans");
  }, []);

  const showCompoundingMessage = useMemo(() => {
    if (profile?.mailingAddress?.state) {
      const excludedCompoundingStates =
        t("SEMAGLUTIDE_INELIGIBLE_STATES")?.split(",") || [];

      return (
        !excludedCompoundingStates.includes(profile.mailingAddress.state) &&
        treatment !== COMPOUND
      );
    }

    return false;
  }, [profile]);

  const onSelection = plan => {
    setSelectedPlan(plan);
  };

  const continueWithPlan = () => {
    // Active Core/Premium user.
    // Users who just signed up would have these values undefined.
    if (isActiveUser) {
      const aid = getAidFromCookie(newQuiz, baseUrl);
      window.location.href = `${t("SWITCH_OFFER")}${selectedPlan.id}&fromNewUserAssessment=true${aid ? `&aid=${aid}` : ""}`;
      return;
    }

    history.push(routes.clinicCheckoutPayment, {
      plan: selectedPlan,
      treatment
    });
  };

  const nextButtonProps = {
    onNext: continueWithPlan,
    label: t("CONTINUE_WITH", {
      planName: selectedPlan?.contentMap.get(OFFER_PLAN_NAME)?.toLowerCase()
    }),
    isDisabled: !selectedPlan,
    "da-category": "quiz",
    "da-action": "continue",
    "da-label": "continue_with_plan"
  };

  const defaultButtonProps = {
    "da-category": "plans",
    "da-label": `plan:${selectedPlan?.contentMap.get(OFFER_PLAN_NAME)?.toLowerCase()}`,
    "da-action": "continue_with_plan"
  };

  const renderFooter = () => {
    if (isUni3or4Quiz)
      return (
        <StickyFooter
          message={{
            label: t("CHECKOUT_TIME"),
            className: styles.stickyFooterMsg
          }}
          nextButtonProps={nextButtonProps}
        />
      );
    else
      return (
        <SequenceFooter className={styles.footer}>
          <RoundButton
            {...defaultButtonProps}
            onClick={continueWithPlan}
            isDisabled={!selectedPlan}
          >
            {t("CONTINUE_WITH", {
              planName: selectedPlan?.contentMap
                .get(OFFER_PLAN_NAME)
                ?.toLowerCase()
            })}
          </RoundButton>
          <p className={styles.callout}>{t("CHECKOUT_TIME")}</p>
        </SequenceFooter>
      );
  };

  const renderContent = () => {
    if (applangaKeyMap[treatment]) {
      return <MedsCard urlParam={treatment} />;
    }
    return (
      <Column className={styles.programSlice}>
        <h1 className={styles.heading}>
          {t("CLINIC_CHECKOUT_PROGRAM_HEADING", {
            firstName: profile?.firstName
          })}
        </h1>
        <ul className={isUni3or4Quiz && styles.bulletList}>
          <BulletItem isCircle={isUni3or4Quiz}>
            {t("CLINIC_CHECKOUT_PROGRAM_BULLET_1")}
          </BulletItem>
          <BulletItem isCircle={isUni3or4Quiz}>
            {t("CLINIC_CHECKOUT_PROGRAM_BULLET_2")}
          </BulletItem>
          <BulletItem isCircle={isUni3or4Quiz}>
            {t("CLINIC_CHECKOUT_PROGRAM_BULLET_3")}
          </BulletItem>
          <BulletItem isCircle={isUni3or4Quiz}>
            {t("CLINIC_CHECKOUT_PROGRAM_BULLET_4")}
          </BulletItem>
          <BulletItem isCircle={isUni3or4Quiz}>
            {t("CLINIC_CHECKOUT_PROGRAM_BULLET_5")}
          </BulletItem>
        </ul>
      </Column>
    );
  };

  return (
    <Loading loading={isLoading}>
      {!isError ? (
        <SiteWrapper size="small">
          <Column
            className={cx(styles.content, {
              [styles.bottomPadding]: isUni3or4Quiz
            })}
          >
            {renderContent()}
            <Column className={styles.plamSlice}>
              <h2 className={styles.heading}>
                {isUni3or4Quiz ? t("YOUR_PLAN") : t("CHECKOUT_CHOOSE_PLAN")}
              </h2>
              <AsHtml value={bundle.contentMap.get(MODAL_DESCRIPTION)} />
              {plans.map(plan => (
                <SimpleOfferCard
                  key={plan.id}
                  plan={plan}
                  onSelection={onSelection}
                  selected={selectedPlan?.id === plan.id}
                />
              ))}
            </Column>
            {showCompoundingMessage && (
              <Message message="SEMAGLUTIDE_UPGRADE_MESSAGE" />
            )}
            {renderFooter()}
          </Column>
        </SiteWrapper>
      ) : (
        <Error error={error} onClick={retry} />
      )}
    </Loading>
  );
};

export default ClinicCheckout;
